import { useCallback, useEffect, useRef, useState } from "react";

interface UseInfiniteScrollProps {
  containerRef: React.RefObject<HTMLElement>;
  initialPageSize?: number;
  pageSize?: number;
  totalItems: number;
}

export function useInfiniteScroll({
  containerRef,
  initialPageSize = 20,
  pageSize = 20,
  totalItems,
}: UseInfiniteScrollProps): {
  hasMoreItems: boolean;
  isLoading: boolean;
  loadingRef: React.RefObject<HTMLDivElement>;
  loadMore: () => Promise<void>;
  visibleItemCount: number;
} {
  const [isLoading, setIsLoading] = useState(false);
  const [visibleItemCount, setVisibleItemCount] = useState(initialPageSize);
  const loadingRef = useRef<HTMLDivElement>(null);

  const hasMoreItems = visibleItemCount < totalItems;

  const loadMore = useCallback(async () => {
    if (isLoading || !hasMoreItems) return;

    setIsLoading(true);

    try {
      const newCount = Math.min(visibleItemCount + pageSize, totalItems);

      setVisibleItemCount(newCount);
    } catch (error) {
      console.error("Error loading more items:", error);
    } finally {
      setIsLoading(false);
    }
  }, [isLoading, hasMoreItems, visibleItemCount, pageSize, totalItems]);

  useEffect(() => {
    if (!hasMoreItems || isLoading) return;

    const options = {
      root: containerRef.current,
      rootMargin: "50px",
      threshold: 0.1,
    };

    const observer = new IntersectionObserver((entries) => {
      const [entry] = entries;

      if (entry?.isIntersecting && !isLoading && hasMoreItems) {
        loadMore();
      }
    }, options);

    if (loadingRef.current && hasMoreItems) {
      observer.observe(loadingRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [loadMore, isLoading, hasMoreItems]);

  return {
    hasMoreItems,
    isLoading,
    loadingRef,
    loadMore,
    visibleItemCount,
  };
}
